import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from './format';
import { Icon } from './icons';
import { Button, type ButtonVariant, Input } from './primitives';

/* ----------------------------------- Modal ---------------------------------- */

/** Masaüstünde ortada, mobilde alttan açılan sayfa. Esc ve arka plan tıklaması kapatır. */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissible = true,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  dismissible?: boolean;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !dismissible) return;
      const dialogs = document.querySelectorAll('[role="dialog"]');
      if (dialogs[dialogs.length - 1] === panelRef.current) onClose();
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusable = panelRef.current?.querySelector<HTMLElement>('input, select, textarea, button:not([data-close])');
    (focusable ?? panelRef.current)?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      prev?.focus?.();
    };
  }, [open, dismissible, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      <div className="ui-anim-fade-in absolute inset-0 bg-[var(--overlay)]" onClick={dismissible ? onClose : undefined} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className={cn(
          'ui-anim-slide-up pb-safe relative flex max-h-[92dvh] w-full flex-col rounded-t-[var(--radius)] border border-border bg-card shadow-xl outline-none sm:rounded-[var(--radius)]',
          size === 'sm' && 'sm:max-w-sm',
          size === 'md' && 'sm:max-w-lg',
          size === 'lg' && 'sm:max-w-2xl',
          size === 'xl' && 'sm:max-w-4xl',
        )}
      >
        <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-border sm:hidden" aria-hidden="true" />
        {title ? (
          <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
            <div className="min-w-0">
              <h2 className="text-base font-semibold">{title}</h2>
              {description ? <p className="mt-0.5 text-sm text-muted">{description}</p> : null}
            </div>
            {dismissible ? (
              <button data-close type="button" onClick={onClose} className="-mr-1 rounded p-1.5 text-muted hover:bg-surface hover:text-foreground" aria-label="Kapat">
                <Icon name="x" />
              </button>
            ) : null}
          </div>
        ) : null}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">{children}</div>
        {footer ? <div className="flex flex-col-reverse gap-2 border-t border-border px-4 py-3 sm:flex-row sm:justify-end">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

/** Mobilde soldan açılan çekmece. */
export function Drawer({ open, onClose, children, side = 'left' }: { open: boolean; onClose: () => void; children: ReactNode; side?: 'left' | 'right' }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50">
      <div className="ui-anim-fade-in absolute inset-0 bg-[var(--overlay)]" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        className={cn('ui-anim-drawer pt-safe pb-safe absolute top-0 bottom-0 flex w-[85vw] max-w-xs flex-col border-border bg-card shadow-xl', side === 'left' ? 'left-0 border-r' : 'right-0 border-l')}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

/* ----------------------------------- Toast ---------------------------------- */

type ToastTone = 'success' | 'error' | 'info';
interface ToastItem {
  id: number;
  tone: ToastTone;
  message: ReactNode;
}

interface ToastApi {
  success: (m: ReactNode) => void;
  error: (m: ReactNode) => void;
  info: (m: ReactNode) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const push = useCallback((tone: ToastTone, message: ReactNode) => {
    const id = ++seq.current;
    setItems((prev) => [...prev.slice(-3), { id, tone, message }]);
    setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), tone === 'error' ? 6000 : 3500);
  }, []);
  const api = useMemo<ToastApi>(() => ({ success: (m) => push('success', m), error: (m) => push('error', m), info: (m) => push('info', m) }), [push]);
  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="pointer-events-none fixed inset-x-0 bottom-20 z-[60] flex flex-col items-center gap-2 px-4 md:right-4 md:bottom-4 md:left-auto md:items-end" aria-live="polite">
          {items.map((t) => (
            <div
              key={t.id}
              className={cn(
                'ui-anim-slide-up pointer-events-auto flex w-full max-w-sm items-start gap-2 rounded-[var(--radius)] border bg-card px-3 py-2.5 text-sm shadow-lg',
                t.tone === 'success' && 'border-success/40',
                t.tone === 'error' && 'border-danger/40',
                t.tone === 'info' && 'border-border',
              )}
              role={t.tone === 'error' ? 'alert' : 'status'}
            >
              <Icon
                name={t.tone === 'success' ? 'check' : t.tone === 'error' ? 'alert' : 'info'}
                className={cn('mt-0.5 shrink-0', t.tone === 'success' && 'text-success', t.tone === 'error' && 'text-danger', t.tone === 'info' && 'text-primary')}
              />
              <div className="min-w-0 flex-1">{t.message}</div>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('ToastProvider eksik');
  return ctx;
}

/* --------------------------------- Confirm ---------------------------------- */

interface ConfirmOptions {
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ButtonVariant;
  /** Verilirse kullanıcı bu metni yazmadan onaylayamaz (geri alınamaz işlemler). */
  typeToConfirm?: string;
}

type ConfirmFn = (o: ConfirmOptions) => Promise<boolean>;
const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<(ConfirmOptions & { resolve: (v: boolean) => void }) | null>(null);
  const [typed, setTyped] = useState('');
  const confirm = useCallback<ConfirmFn>(
    (o) =>
      new Promise((resolve) => {
        setTyped('');
        setState({ ...o, resolve });
      }),
    [],
  );
  const close = (v: boolean) => {
    state?.resolve(v);
    setState(null);
  };
  const blocked = !!state?.typeToConfirm && typed.trim() !== state.typeToConfirm;
  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        open={!!state}
        onClose={() => close(false)}
        title={state?.title}
        size="sm"
        footer={
          <>
            <Button onClick={() => close(false)}>{state?.cancelLabel ?? 'Vazgeç'}</Button>
            <Button variant={state?.variant ?? 'primary'} disabled={blocked} onClick={() => close(true)}>
              {state?.confirmLabel ?? 'Onayla'}
            </Button>
          </>
        }
      >
        {state?.description ? <div className="text-sm text-muted">{state.description}</div> : null}
        {state?.typeToConfirm ? (
          <div className="mt-3">
            <Input label={`Onaylamak için "${state.typeToConfirm}" yazın`} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
          </div>
        ) : null}
      </Modal>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('ConfirmProvider eksik');
  return ctx;
}

/* ------------------------------- Dropdown menu ------------------------------ */

export function Menu({ trigger, items, align = 'right' }: { trigger: ReactNode; items: { label: ReactNode; onClick: () => void; danger?: boolean; hidden?: boolean }[]; align?: 'left' | 'right' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <div ref={ref} className="relative inline-block">
      <div onClick={() => setOpen((o) => !o)}>{trigger}</div>
      {open ? (
        <div
          role="menu"
          className={cn('ui-anim-fade-in absolute z-40 mt-1 min-w-48 overflow-hidden rounded-[var(--radius)] border border-border bg-card py-1 shadow-lg', align === 'right' ? 'right-0' : 'left-0')}
        >
          {items
            .filter((i) => !i.hidden)
            .map((it, i) => (
              <button
                key={i}
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  it.onClick();
                }}
                className={cn('block min-h-10 w-full px-3 text-left text-sm hover:bg-surface', it.danger && 'text-danger')}
              >
                {it.label}
              </button>
            ))}
        </div>
      ) : null}
    </div>
  );
}
